import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveProject,
  fetchTeamMembers,
  fetchTeams,
  fetchProjectsByTeam,
  fetchProjectById,
  fetchSystemTeamsPaged,
  leaveProject,
  leaveTeam,
  restoreProject,
  transferProject,
  updateProject,
  updateTeam,
  type PagedData,
  type Project,
  type ProjectArchiveFilter,
  type SystemTeam,
  type Team,
  type TeamMember,
} from '../../lib/api';
import type { ProjectPermission } from '../../lib/api/projectPermissions';
import { projectAccessQueryOptions, isProjectAccessDeniedError } from './projectAccessQuery';
import { type SystemTeamQueryParams, queryKeys } from './queryKeys';

const workspaceShellStaleTime = 10 * 60 * 1000;

export const useTeams = (token: string | null, options?: { enabled?: boolean; refetchOnWindowFocus?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.teams.all,
    queryFn: () => fetchTeams(token as string),
    enabled: Boolean(token) && (options?.enabled ?? true),
    refetchOnWindowFocus: options?.refetchOnWindowFocus,
    staleTime: workspaceShellStaleTime,
  });
};

export const useProjects = (token: string | null, teamId: string, requiredPermission?: ProjectPermission) => {
  const projectsQuery = useQuery({
    queryKey: queryKeys.projects.byTeam(teamId),
    queryFn: () => fetchProjectsByTeam(token as string, teamId),
    enabled: Boolean(token) && teamId.length > 0,
    staleTime: workspaceShellStaleTime,
  });
  const accessQueries = useQueries({
    queries: requiredPermission
      ? (projectsQuery.data ?? []).map((project) => projectAccessQueryOptions(token, project.id))
      : [],
  });
  if (!requiredPermission) return projectsQuery;
  return {
    ...projectsQuery,
    data: projectsQuery.data?.filter((_project, index) => {
      const access = accessQueries[index];
      return !isProjectAccessDeniedError(access?.error) && access?.data?.permissions.includes(requiredPermission);
    }),
    isLoading: projectsQuery.isLoading || accessQueries.some((access) => access.isPending),
    error:
      projectsQuery.error ??
      accessQueries.find((access) => access.error && !isProjectAccessDeniedError(access.error))?.error ??
      null,
  };
};

export const useShareableTeamProjects = (token: string | null, teamId: string) => {
  return useQuery({
    queryKey: queryKeys.projects.shareableByTeam(teamId),
    queryFn: () => fetchProjectsByTeam(token as string, teamId, { scope: 'team' }),
    enabled: Boolean(token) && teamId.length > 0,
    staleTime: workspaceShellStaleTime,
  });
};

export const useArchivedProjects = (token: string | null, teamId: string, enabled: boolean) => {
  return useQuery({
    queryKey: queryKeys.projects.byTeamWithArchived(teamId, 'archived'),
    queryFn: () => fetchProjectsByTeam(token as string, teamId, { archived: 'archived' as ProjectArchiveFilter }),
    enabled: Boolean(token) && teamId.length > 0 && enabled,
    staleTime: workspaceShellStaleTime,
  });
};

export const useProject = (token: string | null, projectId: string) => {
  return useQuery({
    queryKey: queryKeys.projects.detail(projectId),
    queryFn: () => fetchProjectById(token as string, projectId),
    enabled: Boolean(token) && projectId.length > 0,
    staleTime: workspaceShellStaleTime,
  });
};

export const useTeamMembers = (token: string | null, teamId: string | null) => {
  return useQuery<TeamMember[]>({
    queryKey: queryKeys.teams.members(teamId ?? '__none__'),
    queryFn: () => fetchTeamMembers(token as string, teamId as string),
    enabled: Boolean(token && teamId),
  });
};

export const useSystemTeamsPage = (token: string | null, params: SystemTeamQueryParams = {}) => {
  return useQuery<PagedData<SystemTeam>>({
    queryKey: queryKeys.systemTeams.list(params),
    queryFn: () => fetchSystemTeamsPaged(token as string, params),
    enabled: Boolean(token),
    staleTime: 300_000,
  });
};

type SystemTeamsInfiniteParams = Omit<SystemTeamQueryParams, 'page' | 'pageSize'>;

export const useSystemTeamsInfinite = (
  token: string | null,
  params: SystemTeamsInfiniteParams = {},
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery<
    PagedData<SystemTeam>,
    Error,
    PagedData<SystemTeam>,
    ReturnType<typeof queryKeys.systemTeams.infinite>,
    number
  >({
    queryKey: queryKeys.systemTeams.infinite(params),
    queryFn: ({ pageParam }) => fetchSystemTeamsPaged(token as string, { ...params, page: pageParam, pageSize: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
    enabled: Boolean(token) && (options?.enabled ?? true),
    staleTime: 300_000,
    select: (data) =>
      ({
        pages: data.pages,
        pageParams: data.pageParams,
        data: data.pages.flatMap((page) => page.data),
        meta: data.pages[data.pages.length - 1]?.meta,
      }) as unknown as PagedData<SystemTeam>,
  });
};

export const useUpdateTeamMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<Team, Error, { teamId: string; input: { name?: string; description?: string } }>({
    mutationFn: ({ teamId, input }) => updateTeam(token as string, teamId, input),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.projects });
      void queryClient.invalidateQueries({ queryKey: queryKeys.teams.members(variables.teamId) });
    },
  });
};

export const useLeaveTeamMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { teamId: string }>({
    mutationFn: ({ teamId }) => leaveTeam(token as string, teamId),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.byTeam(variables.teamId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.teams.members(variables.teamId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.projects });
    },
  });
};

export const useUpdateProjectMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<
    Project,
    Error,
    {
      projectId: string;
      teamId: string;
      input: {
        name?: string;
        description?: string;
        tags?: string[];
        language?: Project['language'];
        visibility?: Project['visibility'];
      };
    }
  >({
    mutationFn: ({ projectId, input }) => updateProject(token as string, projectId, input),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.byTeam(variables.teamId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.projects });
    },
  });
};

export const useLeaveProjectMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { projectId: string; teamId: string }>({
    mutationFn: ({ projectId }) => leaveProject(token as string, projectId),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.byTeam(variables.teamId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.members.projectMembers(variables.projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.projects });
    },
  });
};

export const useTransferProjectMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<Project, Error, { projectId: string; sourceTeamId: string; targetTeamId: string }>({
    mutationFn: ({ projectId, targetTeamId }) => transferProject(token as string, projectId, { targetTeamId }),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.byTeam(variables.sourceTeamId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.byTeam(variables.targetTeamId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.projects });
    },
  });
};

export const useArchiveProjectMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<Project, Error, { projectId: string; teamId: string }>({
    mutationFn: ({ projectId }) => archiveProject(token as string, projectId),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.byTeam(variables.teamId) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projects.byTeamWithArchived(variables.teamId, 'archived'),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.projects });
    },
  });
};

export const useRestoreProjectMutation = (token: string | null) => {
  const queryClient = useQueryClient();

  return useMutation<Project, Error, { projectId: string; teamId: string }>({
    mutationFn: ({ projectId }) => restoreProject(token as string, projectId),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.byTeam(variables.teamId) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projects.byTeamWithArchived(variables.teamId, 'archived'),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(variables.projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.me.projects });
    },
  });
};
